import { fetchTourney } from "#/queries";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { enumParam, optionalNumber } from "#/helpers/search-params";
import { formatResultPoints } from "#/helpers/format-result-points";
import { Link } from "#/components/link";
import { Tabs } from "#/components/routed-tabs";
import {
  PaintingLightbox,
  positionLabel,
} from "#/components/painting-lightbox";
import { Image } from "#/components/image";
import { PhotoLightbox } from "#/components/photo-lightbox";
import { SITE_NAME, SITE_URL, absoluteUrl, jsonLd, seo } from "#/helpers/seo";
import { SortableTh, useTableSort } from "#/components/sortable-table";
import type { SportsEvent, WithContext } from "schema-dts";

export const Route = createFileRoute("/event/$id")({
  params: {
    parse: (raw: Record<string, string>) => ({ id: Number(raw.id) }),
    stringify: (params: { id: number }) => ({ id: String(params.id) }),
  },
  validateSearch: (search: Record<string, unknown>) => ({
    tab: enumParam(search.tab, ["results", "best-painted", "photos"] as const),
    painting: optionalNumber(search.painting),
    photo: optionalNumber(search.photo),
  }),
  loader: async ({ params }) => {
    try {
      return await fetchTourney({ data: { id: params.id } });
    } catch {
      throw notFound();
    }
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const t = loaderData.tourney as any;
    const players = loaderData.players as any[];
    const winnerName = players[0]?.playerName;

    const description =
      t.name + t.venue
        ? ` at ${t.venue},`
        : "" +
          ` a ${t.rounds} round Malifaux tournament attended by ${players.length} players.` +
          (winnerName ? ` Won by ${winnerName}.` : "");

    const firstPaintedImage = (loaderData.paintingCategories as any[])
      .flatMap((c: any) => c.winners ?? [])
      .find((w: any) => w.imageKey)?.imageKey;
    const ogImage = firstPaintedImage
      ? `${import.meta.env.VITE_ASSETS_URL}/${firstPaintedImage}-ogp.jpg`
      : undefined;

    const schema: WithContext<SportsEvent> = {
      "@context": "https://schema.org",
      "@type": "SportsEvent",
      name: t.name,
      startDate: t.date,
      url: absoluteUrl(`/event/${params.id}`),
      sport: "Malifaux",
      ...(t.venue
        ? { location: { "@type": "Place", name: t.venue } }
        : { location: { "@type": "Place", name: "United Kingdom" } }),
      competitor: players.slice(0, 5).map((p) => ({
        "@type": "Person",
        name: p.playerName,
        ...(p.playerId ? { url: `${SITE_URL}/player/${p.playerId}` } : {}),
      })),
    };

    return {
      ...seo({
        title: `${t.name} — ${SITE_NAME}`,
        description,
        path: `/event/${params.id}`,
        type: "article",
        image: ogImage,
      }),
      scripts: [jsonLd(schema)],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { players, tourney, paintingCategories, photos } =
    Route.useLoaderData();
  const { painting: activePaintingId, photo: activePhotoId } =
    Route.useSearch();
  const navigate = Route.useNavigate();
  const t = tourney as any;
  const cats = paintingCategories as any[];
  const { rows, getSortProps } = useTableSort(players as any[], {
    place: { value: (r) => r.place },
    name: { value: (r) => r.playerName },
    points: { value: (r) => r.points, natural: "desc" },
    faction: { value: (r) => r.factionName },
  });

  const hasAnyImages = cats.some((cat: any) =>
    (cat.winners ?? []).some((w: any) => w.imageKey),
  );

  const activeWinner = activePaintingId
    ? (cats
        .flatMap((cat: any) =>
          (cat.winners ?? []).map((w: any) => ({
            ...w,
            categoryName: cat.name,
            totalWinners: cat.winners.length,
          })),
        )
        .find((w: any) => w.id === activePaintingId) ?? null)
    : null;

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">{t.name}</h1>
      <Tabs defaultValue="results">
        <Tabs.List mb="md">
          <Tabs.Tab value="results">Results</Tabs.Tab>
          {hasAnyImages && (
            <Tabs.Tab value="best-painted">Best Painted</Tabs.Tab>
          )}
          {photos.length > 0 && <Tabs.Tab value="photos">Photos</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="results">
          <table className="min-w-full text-sm tabular-nums">
            <thead>
              <tr className="border-b border-border text-left">
                <SortableTh {...getSortProps("place")}>Place</SortableTh>
                <SortableTh {...getSortProps("name")}>Name</SortableTh>
                <SortableTh {...getSortProps("points")}>Points</SortableTh>
                <SortableTh {...getSortProps("faction")}>Faction</SortableTh>
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any) => (
                <tr
                  key={`${row.place}-${row.playerName}`}
                  className="border-b border-border"
                >
                  <td className="px-2 py-1.5">{row.place}</td>
                  <td className="px-2 py-1.5">
                    {row.playerId != null ? (
                      <Link
                        to="/player/$id"
                        params={{ id: row.playerId }}
                        search={{
                          tab: undefined,
                          typeCode: undefined,
                          painting: undefined,
                          achievement: undefined,
                        }}
                      >
                        {row.playerName}
                      </Link>
                    ) : (
                      row.playerName
                    )}
                  </td>
                  <td className="px-2 py-1.5">{formatResultPoints(row.points)}</td>
                  <td className="px-2 py-1.5">{row.factionName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tabs.Panel>

        <Tabs.Panel value="best-painted">
          {cats.map((cat: any) => {
            const winnersWithImages = (cat.winners ?? []).filter(
              (w: any) => w.imageKey,
            );
            if (winnersWithImages.length === 0) return null;
            return (
              <div key={cat.id} className="mb-6">
                <h3 className="mb-2 text-lg font-semibold">{cat.name}</h3>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                  {winnersWithImages.map((winner: any) => (
                    <div
                      key={winner.id}
                      className="w-[150px] cursor-pointer"
                      onClick={() =>
                        navigate({
                          search: (prev) => ({ ...prev, painting: winner.id }),
                        })
                      }
                    >
                      <Image
                        imageKey={winner.imageKey}
                        width={winner.imageWidth}
                        height={winner.imageHeight}
                        alt={`${winner.playerName} — ${cat.name}`}
                        fallbackWidth={400}
                        sizes="150px"
                        className="h-auto w-[150px] rounded-sm"
                      />
                      <p className="mt-1 text-center text-xs text-muted-foreground">
                        {positionLabel(winner.position, cat.winners.length)} —{" "}
                        {winner.playerId != null ? (
                          <span onClick={(e) => e.stopPropagation()}>
                            <Link
                              to="/player/$id"
                              params={{ id: winner.playerId }}
                              search={{
                                tab: "painting",
                                typeCode: undefined,
                                painting: undefined,
                                achievement: undefined,
                              }}
                            >
                              {winner.playerName}
                            </Link>
                          </span>
                        ) : (
                          winner.playerName
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </Tabs.Panel>

        <Tabs.Panel value="photos">
          <div className="columns-1 gap-4 sm:columns-2 md:columns-3">
            {photos.map((photo) => (
              <figure key={photo.id} className="mb-4 break-inside-avoid">
                <button
                  type="button"
                  className="block w-full cursor-pointer"
                  onClick={() =>
                    navigate({
                      search: (prev) => ({ ...prev, photo: photo.id }),
                    })
                  }
                >
                  <Image
                    imageKey={photo.imageKey}
                    width={photo.imageWidth}
                    height={photo.imageHeight}
                    alt={photo.caption ?? t.name}
                    fallbackWidth={400}
                    sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                    className="h-auto w-full rounded-sm"
                  />
                </button>
                {photo.caption && (
                  <figcaption className="mt-1 text-center text-xs text-muted-foreground">
                    {photo.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </Tabs.Panel>
      </Tabs>

      <PhotoLightbox
        photos={photos}
        activeId={activePhotoId}
        title={t.name}
        onChange={(id) =>
          navigate({ search: (prev) => ({ ...prev, photo: id }) })
        }
      />

      <PaintingLightbox
        winner={activeWinner}
        onClose={() =>
          navigate({ search: (prev) => ({ ...prev, painting: undefined }) })
        }
        linkPlayerName
      />
    </div>
  );
}
